import React from 'react';
import { motion } from 'framer-motion';

interface AnimatedBackgroundProps {
  children: React.ReactNode;
  variant?: 'hero' | 'section' | 'minimal';
  className?: string;
}

export const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  children,
  variant = 'hero',
  className = '',
}) => {
  const getBackgroundClasses = () => {
    switch (variant) {
      case 'hero':
        return 'bg-gradient-to-br from-emerald-50 via-cyan-50 to-blue-50 relative overflow-hidden';
      case 'section':
        return 'bg-gradient-to-r from-blue-50/50 to-purple-50/50 relative';
      case 'minimal':
        return 'bg-gradient-to-b from-slate-50 to-white relative';
      default:
        return 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden';
    }
  };

  return (
    <div className={`${getBackgroundClasses()} ${className}`}>
      {/* Floating orbs for visual interest */}
      {variant === 'hero' && (
        <>
          {/* Large purple orb */}
          <motion.div
            className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full blur-3xl opacity-20"
            animate={{
              x: [0, 30, 0],
              y: [0, -20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          {/* Medium blue orb */}
          <motion.div
            className="absolute top-1/3 left-10 w-48 h-48 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full blur-2xl opacity-25"
            animate={{
              x: [0, -20, 0],
              y: [0, 25, 0],
              scale: [1, 0.9, 1],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />

          {/* Small accent orb */}
          <motion.div
            className="absolute bottom-20 right-1/4 w-32 h-32 bg-gradient-to-r from-teal-400 to-emerald-400 rounded-full blur-xl opacity-30"
            animate={{
              x: [0, 15, 0],
              y: [0, -15, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "linear",
              delay: 2,
            }}
          />

          {/* Geometric shapes for modern feel */}
          <motion.div
            className="absolute top-1/4 right-1/3 w-16 h-16 border-2 border-emerald-300/40 rotate-45 shadow-lg"
            animate={{
              rotate: [45, 225, 45],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "linear",
            }}
          />

          <motion.div
            className="absolute bottom-1/3 left-1/4 w-12 h-12 bg-gradient-to-r from-cyan-300 to-teal-300 rounded-full opacity-40 shadow-sm"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.4, 0.6, 0.4],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </>
      )}

      {/* Grid pattern overlay for texture */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};
